import { t } from '../../../shared/config/translations'
import {
	getCollectionSortFilterOption,
	getCollectionSortSpec,
	normalizeRatingFilters,
} from '../../../shared/filters/collectionFilters'
import type {
	CollectionSort,
	MediaFilterOptions,
	StatusFilterOptions,
} from '../../../shared/types/collection'
import type { DiscogsFolder } from '../../../shared/types/discogs'
import { getDiscogsFolders } from '../../api/collection'
import {
	bindMultiSelectOutsideDismiss,
	closeMultiSelects,
	filterMultiSelectOptions,
	getNextMultiSelectSelection,
	handleMultiSelectEscape,
	handleMultiSelectNavigation,
	syncMultiSelect,
	toggleMultiSelect,
} from '../inputs/MultiSelect'
import {
	DEFAULT_FILTERS_STATE,
	type FiltersOptions,
	type FiltersState,
	hasActiveFilters,
} from './filterState'
import {
	filtersTemplate,
	renderFolderFilterWithOptions,
} from './filterTemplates'

type InitFiltersArgs = {
	signal?: AbortSignal
	containerId?: string
	rootEl?: ParentNode
	initialState?: FiltersState
	onChange: (
		state: FiltersState,
		changedKey?: keyof FiltersState,
	) => Promise<void> | void
	options?: FiltersOptions
}

type FiltersControllerArgs = Pick<
	InitFiltersArgs,
	'initialState' | 'onChange' | 'options' | 'signal'
>

export type FiltersHandle = {
	setState(state: FiltersState, notify?: boolean): Promise<void>
	refreshFolders(): Promise<void>
	dispose(): void
}

let nextFiltersControllerId = 0

class FiltersController implements FiltersHandle {
	private readonly events = new AbortController()
	private readonly rootEl: HTMLElement
	private readonly controlIdPrefix: string
	private state: FiltersState
	private folderOptions: DiscogsFolder[] | null = null
	private readonly onChange: InitFiltersArgs['onChange']
	private readonly options: FiltersOptions | undefined

	constructor(rootEl: HTMLElement, args: FiltersControllerArgs) {
		if (args.signal?.aborted) this.events.abort()
		else
			args.signal?.addEventListener('abort', () => this.dispose(), {
				once: true,
				signal: this.events.signal,
			})
		this.rootEl = rootEl
		this.controlIdPrefix = `filters-${++nextFiltersControllerId}`
		this.onChange = args.onChange
		this.options = args.options

		this.state = {
			...DEFAULT_FILTERS_STATE,
			...(args.initialState ?? {}),
		}

		this.handleChange = this.handleChange.bind(this)
		this.handleClick = this.handleClick.bind(this)
		this.handleKeydown = this.handleKeydown.bind(this)
	}

	/** Loads dynamic options, renders controls, and connects event handlers. */
	async init(): Promise<void> {
		await this.loadFolderOptions()
		if (this.events.signal.aborted) return
		this.render()
		this.addEventListeners()
	}

	/** Replaces all filter values and optionally notifies the owning page. */
	async setState(state: FiltersState, notify = true): Promise<void> {
		if (this.events.signal.aborted) return
		this.state = { ...DEFAULT_FILTERS_STATE, ...state }
		this.render()
		if (notify) await this.onChange(this.state)
	}

	dispose(): void {
		this.events.abort()
	}

	async refreshFolders(): Promise<void> {
		await this.loadFolderOptions()
		if (this.events.signal.aborted) return
		const slot = this.rootEl.querySelector('[data-folder-slot]')
		if (slot) slot.innerHTML = this.renderFolders()
		this.updateStyleFilterLayout()
	}

	private renderFolders(): string {
		return this.folderOptions
			? renderFolderFilterWithOptions(
					this.folderOptions,
					this.state.folderId,
				)
			: ''
	}

	private render(): void {
		this.rootEl.innerHTML = filtersTemplate(
			this.state,
			this.options ?? {},
			this.controlIdPrefix,
			this.renderFolders(),
		)
		this.updateStyleFilterLayout()
	}

	private updateStyleFilterLayout(): void {
		const dropdowns = Array.from(
			this.rootEl.querySelectorAll<HTMLElement>(
				':scope > .tt-select, :scope > [data-folder-slot] > .tt-select',
			),
		)
		const styleIndex = dropdowns.findIndex(
			(control) => control.dataset.selectId === 'col-style-filter',
		)
		this.rootEl.classList.toggle(
			'filters-unpaired-style',
			styleIndex >= 0 && styleIndex % 2 === 0,
		)
	}

	private addEventListeners(): void {
		const { signal } = this.events
		this.rootEl.addEventListener(
			'input',
			(event) => {
				const target = event.target
				if (
					!(target instanceof HTMLInputElement) ||
					!target.matches('.multi-select-search')
				)
					return
				const control = target.closest<HTMLElement>('.tt-multi-select')
				if (control) filterMultiSelectOptions(control)
			},
			{ signal },
		)
		this.rootEl.addEventListener('change', this.handleChange, { signal })
		this.rootEl.addEventListener('click', this.handleClick, { signal })
		this.rootEl.addEventListener('keydown', this.handleKeydown, { signal })
		bindMultiSelectOutsideDismiss()
	}

	/** Closes an open multi-select with Escape without closing a parent overlay. */
	private handleKeydown(event: KeyboardEvent): void {
		if (!handleMultiSelectEscape(this.rootEl, event))
			handleMultiSelectNavigation(event)
	}

	private async handleChange(event: Event): Promise<void> {
		const target = event.target
		if (target instanceof HTMLSelectElement) {
			await this.handleSelectChange(target)
			return
		}

		if (!(target instanceof HTMLInputElement)) return

		if (target.dataset.filterKey === 'favoritesOnly') {
			await this.updateState(
				{ favoritesOnly: target.checked },
				'favoritesOnly',
			)
			return
		}

		if (target.dataset.filterKey === 'includeHidden') {
			this.rootEl
				.querySelector('.hidden-filter-icon use')
				?.setAttribute(
					'href',
					target.checked ? '#symbols-eye' : '#symbols-eye-closed',
				)
			await this.updateState(
				{ includeHidden: target.checked },
				'includeHidden',
			)
		}
	}

	private async handleSelectChange(target: HTMLSelectElement): Promise<void> {
		switch (target.id) {
			case 'col-sort-by-filter':
				await this.handleSortChange(target)
				break
			case 'col-year-from-filter':
				await this.updateState(
					{ yearFrom: this.parseYearValue(target.value) },
					'yearFrom',
				)
				break
			case 'col-year-to-filter':
				await this.updateState(
					{ yearTo: this.parseYearValue(target.value) },
					'yearTo',
				)
				break
		}
	}

	private async handleClick(event: Event): Promise<void> {
		const target = event.target as HTMLElement | null
		if (await this.handleMultiSelectClick(target)) return

		if (target?.closest('[data-filters-reset]')) {
			await this.resetFilters()
			return
		}

		if (this.handleFilterSwitchCardClick(target)) return

		const sortDirectionButton = target?.closest<HTMLButtonElement>(
			'#col-sort-direction',
		)
		if (sortDirectionButton) {
			await this.handleSortDirectionChange(sortDirectionButton)
			return
		}

		const dimReleasesButton = target?.closest<HTMLButtonElement>(
			'#col-dim-releases-filter',
		)
		if (!dimReleasesButton) return

		const nextDimReleases = !this.state.dimReleases

		const nextLabel = nextDimReleases
			? t('collection.undimReleasesFilter')
			: t('collection.dimReleasesFilter')

		const tooltip = dimReleasesButton.querySelector<HTMLElement>('.tooltip')
		if (tooltip) tooltip.textContent = nextLabel

		const useEl = dimReleasesButton.querySelector('use')
		useEl?.setAttribute(
			'href',
			nextDimReleases ? '#symbols-eye-closed' : '#symbols-eye',
		)
		await this.updateState({ dimReleases: nextDimReleases }, 'dimReleases')
	}

	private handleFilterSwitchCardClick(target: HTMLElement | null): boolean {
		const card = target?.closest<HTMLElement>('.filter-switch')
		if (!card) return false

		// Native controls already handle their own clicks; buttons such as the
		// hidden-release help trigger must remain independent from the switch.
		if (target?.closest('button, input, label, a')) return false

		const input = card.querySelector<HTMLInputElement>(
			'input[type="checkbox"]',
		)
		if (!input || input.disabled) return false

		input.click()
		return true
	}

	/** Updates the selected sort and temporarily disables the control while data reloads. */
	private async handleSortChange(target: HTMLSelectElement): Promise<void> {
		const { sortOrder } = getCollectionSortSpec(this.state.sort)
		const value = getCollectionSortFilterOption(
			target.value as CollectionSort,
			sortOrder,
		)
		this.setSortControlsDisabled(true)
		try {
			await this.updateState({ sort: value }, 'sort')
		} finally {
			this.setSortControlsDisabled(false)
		}
	}

	private async handleSortDirectionChange(
		button: HTMLButtonElement,
	): Promise<void> {
		const { sort, sortOrder } = getCollectionSortSpec(this.state.sort)
		const nextOrder = sortOrder === 'asc' ? 'desc' : 'asc'
		button.setAttribute('aria-pressed', String(nextOrder === 'desc'))
		this.setSortControlsDisabled(true)
		try {
			await this.updateState(
				{ sort: getCollectionSortFilterOption(sort, nextOrder) },
				'sort',
			)
		} finally {
			this.setSortControlsDisabled(false)
		}
	}

	private setSortControlsDisabled(disabled: boolean): void {
		const select = this.rootEl.querySelector<HTMLSelectElement>(
			'#col-sort-by-filter',
		)
		const button = this.rootEl.querySelector<HTMLButtonElement>(
			'#col-sort-direction',
		)
		if (select) select.disabled = disabled
		if (button) button.disabled = disabled
	}

	private async handleMultiSelectClick(
		target: HTMLElement | null,
	): Promise<boolean> {
		const trigger = target?.closest<HTMLButtonElement>(
			'.tt-multi-select .multi-select-trigger',
		)
		if (trigger) {
			toggleMultiSelect(this.rootEl, trigger)
			return true
		}

		const searchable = target?.closest<HTMLElement>(
			'.tt-multi-select[data-searchable="true"]',
		)
		if (searchable && target?.closest('.multi-select-clear-search')) {
			const input = searchable.querySelector<HTMLInputElement>(
				'.multi-select-search',
			)
			if (input) {
				input.value = ''
				input.focus()
			}
			filterMultiSelectOptions(searchable)
			return true
		}

		const option = target?.closest<HTMLButtonElement>(
			'.tt-multi-select .multi-select-option',
		)
		if (!option) {
			if (searchable) return true
			if (target && this.rootEl.contains(target)) {
				closeMultiSelects(this.rootEl)
			}
			return false
		}

		const control = option.closest<HTMLElement>('.tt-multi-select')
		if (!control) return true

		const selectId = control.dataset.selectId
		const { values: nextValues, selectsAll } = getNextMultiSelectSelection(
			control,
			option,
		)

		syncMultiSelect(control, nextValues, selectsAll)

		await this.applyMultiSelectValues(
			control,
			selectId,
			nextValues,
			selectsAll,
		)

		return true
	}

	private async applyMultiSelectValues(
		control: HTMLElement,
		selectId: string | undefined,
		nextValues: string[],
		selectsAll: boolean,
	): Promise<void> {
		switch (selectId) {
			case 'col-media-filter':
				await this.updateState(
					{
						media: toFilterStateValue<MediaFilterOptions>(
							nextValues,
							'all',
							selectsAll,
						),
					},
					'media',
				)
				break
			case 'col-folder-filter':
				await this.updateState(
					{
						folderId: selectsAll
							? null
							: this.toNumberFilterStateValue(nextValues),
					},
					'folderId',
				)
				break
			case 'col-status-filter': {
				const includesFavoritesOnly = Boolean(
					control.querySelector(
						'.multi-select-option[data-value="favoritesOnly"]',
					),
				)
				await this.updateState(
					{
						status: toFilterStateValue<StatusFilterOptions>(
							nextValues.filter(
								(value) => value !== 'favoritesOnly',
							),
							'all',
							selectsAll,
						),
						...(includesFavoritesOnly
							? {
									favoritesOnly:
										nextValues.includes('favoritesOnly'),
								}
							: {}),
					},
					'status',
				)
				break
			}
			case 'col-rating-filter':
				await this.updateState(
					{
						rating: selectsAll
							? undefined
							: (normalizeRatingFilters(nextValues) ?? []),
					},
					'rating',
				)
				break
			case 'col-genre-filter':
				await this.updateState(
					{
						genre: this.toOptionalArray(nextValues, selectsAll),
					},
					'genre',
				)
				break
			case 'col-style-filter':
				await this.updateState(
					{
						style: this.toOptionalArray(nextValues, selectsAll),
					},
					'style',
				)
				break
		}
	}

	private toNumberFilterStateValue(values: string[]): number[] {
		const numbers = values.map(Number).filter(Number.isFinite)
		return numbers
	}

	private toOptionalArray<T>(
		values: T[],
		selectsAll: boolean,
	): T[] | undefined {
		return selectsAll ? undefined : values
	}

	private parseYearValue(value: string): number | undefined {
		return value === '-' ? undefined : Number(value)
	}

	private async updateState(
		patch: Partial<FiltersState>,
		changedKey: keyof FiltersState,
	): Promise<void> {
		this.state = { ...this.state, ...patch }
		this.updateResetButtonVisibility()
		await this.onChange(this.state, changedKey)
	}

	private updateResetButtonVisibility(): void {
		const button = this.rootEl.querySelector<HTMLButtonElement>(
			'[data-filters-reset]',
		)
		const resetState = this.options?.resetState
		if (!button || !resetState) return

		const isVisible = hasActiveFilters(this.state, resetState)
		button.classList.toggle('is-hidden', !isVisible)
	}

	/** Restores the configured filter values and refreshes the caller. */
	private async resetFilters(): Promise<void> {
		const resetState = this.options?.resetState
		if (!resetState) return

		this.state = { ...this.state, ...resetState }
		this.render()
		await this.onChange(this.state)
	}

	private async loadFolderOptions(): Promise<void> {
		const showFolder = this.options?.showFolder ?? true
		if (!showFolder) return

		try {
			const res = await getDiscogsFolders()
			this.folderOptions = res.folders
			if ((this.folderOptions?.length ?? 0) === 0) {
				this.folderOptions = null
			}
		} catch (err) {
			console.error('Failed to load folders', err)
			this.folderOptions = null
		}
	}
}

export async function initFilters({
	containerId = 'filter-container',
	initialState,
	onChange,
	options,
	rootEl,
	signal,
}: InitFiltersArgs): Promise<FiltersHandle | null> {
	const container = getFilterContainer(containerId, rootEl)
	if (!container) return null

	const controller = new FiltersController(container, {
		signal,
		initialState,
		onChange,
		options,
	})
	await controller.init()
	return controller
}

function getFilterContainer(
	containerId: string,
	rootEl?: ParentNode,
): HTMLElement | null {
	return (
		rootEl?.querySelector<HTMLElement>(`#${containerId}`) ??
		document.getElementById(containerId)
	)
}

function toFilterStateValue<T extends string>(
	values: string[],
	defaultValue: T,
	selectsAll: boolean,
): T | T[] {
	if (selectsAll) return defaultValue
	return values as T[]
}
